import { Stethoscope, Scissors, SmilePlus, Wrench, Sparkles, MapPin } from "lucide-react";

const useCases = [
  {
    icon: Stethoscope,
    title: "Clinics & Medical Practices",
    description: "Handle patient enquiries, book consultations, and capture new patient details without tying up your front desk staff.",
    stats: "Avg. 47 calls handled per day",
    color: "border-l-blue-500",
    bg: "bg-blue-50",
    iconBg: "bg-blue-100",
    iconColor: "text-blue-600",
  },
  {
    icon: Scissors,
    title: "Salons & Barbershops",
    description: "Let the AI book haircuts, colour appointments, and consultations while your stylists focus on the clients in the chair.",
    stats: "38% more appointments booked",
    color: "border-l-purple-500",
    bg: "bg-purple-50",
    iconBg: "bg-purple-100",
    iconColor: "text-purple-600",
  },
  {
    icon: SmilePlus,
    title: "Dental Offices",
    description: "Answer new patient calls, schedule cleanings and check-ups, and send appointment reminders — all automatically.",
    stats: "Zero missed patient calls",
    color: "border-l-green-500",
    bg: "bg-green-50",
    iconBg: "bg-green-100",
    iconColor: "text-green-600",
  },
  {
    icon: Wrench,
    title: "Repair & Home Services",
    description: "Capture service requests, check technician availability, and book repair visits without a dispatcher.",
    stats: "Reduce scheduling time by 80%",
    color: "border-l-amber-500",
    bg: "bg-amber-50",
    iconBg: "bg-amber-100",
    iconColor: "text-amber-600",
  },
  {
    icon: Sparkles,
    title: "Cleaning Companies",
    description: "Handle booking requests for one-off and recurring cleans. Capture property details and preferred times automatically.",
    stats: "3x faster quote-to-booking",
    color: "border-l-teal-500",
    bg: "bg-teal-50",
    iconBg: "bg-teal-100",
    iconColor: "text-teal-600",
  },
  {
    icon: MapPin,
    title: "Local Service Providers",
    description: "Whether you're a plumber, electrician, or personal trainer — the AI handles your calls so you can focus on the job.",
    stats: "Never miss a lead again",
    color: "border-l-red-500",
    bg: "bg-red-50",
    iconBg: "bg-red-100",
    iconColor: "text-red-600",
  },
];

export default function UseCasesSection() {
  return (
    <section id="use-cases" data-testid="use-cases-section" className="py-10 md:py-[85px] bg-white">
      <div className="section-container">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 lg:gap-16">
          <div className="lg:col-span-2 lg:sticky lg:top-32 lg:self-start">
            <span className="inline-block text-xs font-medium uppercase tracking-widest text-[#3B82F6] mb-4">
              Who It's For
            </span>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-semibold text-[#0F172A] tracking-tight text-balance mb-4">
              Built for Businesses That{" "}
              <span className="text-[#3B82F6]">Run on Appointments</span>
            </h2>
            <p className="text-base text-slate-600 leading-relaxed">
              If your business takes calls, books appointments, or captures leads — AICallFront was built for you.
            </p>
          </div>

          <div className="lg:col-span-3 space-y-4">
            {useCases.map((useCase, index) => {
              const Icon = useCase.icon;
              return (
                <div
                  key={useCase.title}
                  className={`border-l-4 ${useCase.color} ${useCase.bg} rounded-r-xl p-5 md:p-6 lg:pr-[79px] transition-all hover:shadow-md`}
                  data-testid={`use-case-card-${index}`}
                >
                  <div className="flex items-start gap-4">
                    <div className={`w-10 h-10 rounded-lg ${useCase.iconBg} flex items-center justify-center shrink-0`}>
                      <Icon className={`w-5 h-5 ${useCase.iconColor}`} strokeWidth={1.5} />
                    </div>
                    <div>
                      <h3 className="font-heading font-semibold text-[#0F172A] mb-1">{useCase.title}</h3>
                      <p className="text-sm text-slate-600 leading-relaxed mb-2">{useCase.description}</p>
                      <span className="font-mono text-xs text-slate-400">{useCase.stats}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
